import React, { useState, useEffect } from 'react';
import {
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Autocomplete
} from '@mui/material';
import {
  PushPin,
  PushPinOutlined,
  Add,
  CalendarMonth
} from '@mui/icons-material';
import ChatThread from './ChatThread';
import { ChatRoom } from '../../types/chat.types';
import chatApi from '../../utils/chatApi';
import * as userApi from '../../utils/userApi';
import useAuthStore from '../../store/zustand/auth';
import { useChatStore } from '../../store/useChatStore';
import { useSocket } from '../../hooks/useSocket';
// import { Assignments } from '../assignments/assignments.entity';

interface ChatViewProps {
  companyId: string;
  userId: string;
  userLanguage: string;
}

// Helper function to generate initials from room name
const getInitials = (name: string): string => {
  if (!name) return '?';

  const words = name
    .replace(/[^a-zA-Z0-9\s&]/g, '')
    .split(/[\s&]+/)
    .filter((word) => word.length > 0);

  if (words.length === 1) {
    return words[0].substring(0, 2).toUpperCase();
  }
  if (words.length >= 2) {
    return words
      .slice(0, Math.min(3, words.length))
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  }

  return name.substring(0, 2).toUpperCase();
};

// Generate consistent color based on string
const stringToColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i += 1) {
    hash = str.charCodeAt(i) + (hash * 31 - hash);
  }

  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 65%, 50%)`;
};

function ChatView({ companyId, userId, userLanguage }: ChatViewProps) {
  // FIXED: Initialize socket at top level so it's always listening
  const { joinRoom, leaveRoom } = useSocket({
    companyId,
    enabled: true,
    userId
  });

  // Force re-render on ANY rooms change
  const rooms = useChatStore((state) => state.rooms);
  useChatStore((state) => state.unreadCounts); // keep subscription for badge re-renders
  const onlineUsers = useChatStore((state) => state.onlineUsers);
  const setRooms = useChatStore((state) => state.setRooms);
  const clearUnread = useChatStore((state) => state.clearUnread);

  const [selectedRoom, setSelectedRoom] = useState<ChatRoom | null>(null);
  const [users, setUsers] = useState<any[]>([]);
  const [preferredLanguage, setPreferredLanguage] =
    useState<string>(userLanguage);
  const updateUserInStore = useAuthStore((state) => state.setUser);
  const userState = useAuthStore((state) => state);

  // Pagination state
  const [showAllChannels, setShowAllChannels] = useState(false);
  const [showAllChats, setShowAllChats] = useState(false);

  // Create Room Dialog State
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newRoomName, setNewRoomName] = useState('');
  const [createError, setCreateError] = useState('');
  const [newRoomType, setNewRoomType] = useState<'group' | 'direct'>('group');
  const [selectedParticipants, setSelectedParticipants] = useState<any[]>([]); // Store user objects

  const loadRooms = async () => {
    try {
      const roomData = await chatApi.getRooms();
      setRooms(roomData);

      // FIXED: Join ALL rooms so we receive WebSocket events for all of them
      roomData.forEach((room: ChatRoom) => {
        joinRoom(room.id);
      });

      if (roomData.length > 0 && selectedRoom === null) {
        setSelectedRoom(roomData[0]);
        clearUnread(roomData[0].id);
      } else if (selectedRoom !== null) {
        // On reload (e.g. after room created), keep clearing the active room
        clearUnread(selectedRoom.id);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load rooms:', error);
    }
  };

  const loadUsers = async () => {
    try {
      const userData = await chatApi.getUsers();
      setUsers(userData.filter((u: any) => u.active));
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to load users:', error);
    }
  };

  useEffect(() => {
    loadRooms();
    loadUsers();
  }, []);

  useEffect(() => {
    setPreferredLanguage(userLanguage);
  }, [userLanguage]);

  const handleLanguageChange = async (event: any) => {
    const newLanguage = event.target.value;
    setPreferredLanguage(newLanguage);

    try {
      await userApi.updateLanguagePreference(newLanguage);
      updateUserInStore({
        ...userState,
        preferredLanguage: newLanguage
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to update language preference:', error);
    }
  };

  const handleRoomSelect = (room: ChatRoom) => {
    setSelectedRoom(room);
    clearUnread(room.id);
    // Stamp lastReadAt on the backend so unread count resets on next refresh
    chatApi.markRoomAsRead(room.id).catch(() => {
      // non-critical — badge may reappear on refresh but chat still works
    });
  };

  const handleTogglePin = async (
    roomId: string,
    currentPinned: boolean,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();

    try {
      await chatApi.togglePin(roomId, !currentPinned);

      const updatedRooms = rooms.map((room) =>
        room.id === roomId ? { ...room, isPinned: !currentPinned } : room
      );
      setRooms(updatedRooms);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to toggle pin:', error);
    }
  };

  const handleOpenCreateDialog = () => {
    setCreateDialogOpen(true);
    setNewRoomName('');
    setNewRoomType('group');
    setSelectedParticipants([]);
  };

  const handleCloseCreateDialog = () => {
    setCreateDialogOpen(false);
    setCreateError('');
  };

  const handleCreateRoom = async () => {
    if (!newRoomName.trim()) {
      setCreateError('Please enter a room name');
      return;
    }

    if (selectedParticipants.length === 0) {
      setCreateError('Please select at least one participant');
      return;
    }

    setCreateError('');

    try {
      const participantIds = selectedParticipants.map((p) => p.id);

      await chatApi.createRoom({
        name: newRoomName.trim(),
        participantIds,
        type: newRoomType
      });
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(
        'Room creation returned error (but room may have been created):',
        error
      );
    } finally {
      // Always reload rooms and close dialog, even if API returned error
      // (Room is actually created despite the 500 error)
      await loadRooms();
      handleCloseCreateDialog();

      // Try to select the newly created room
      const roomData = await chatApi.getRooms();
      const newRoom = roomData.find(
        (r: ChatRoom) => r.name === newRoomName.trim()
      );
      if (newRoom) {
        setSelectedRoom(newRoom);
      }
    }
  };

  // Separate and sort rooms
  const channelRooms = rooms
    .filter((r) => r.type === 'group' || r.type === 'project')
    .sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return a.name.localeCompare(b.name);
    });

  const directRooms = rooms
    .filter((r) => r.type === 'direct')
    .sort((a, b) => {
      if (a.isPinned && !b.isPinned) return -1;
      if (!a.isPinned && b.isPinned) return 1;
      return a.name.localeCompare(b.name);
    });

  const renderRoomItem = (room: ChatRoom, isDirect: boolean = false) => {
    let displayName = room.name;
    let initials = getInitials(room.name);
    let otherUser: any = null;

    if (isDirect) {
      const otherUserId = room.participantIds?.find((id) => id !== userId);
      otherUser = users.find((u) => u.id === otherUserId);
      displayName = otherUser
        ? `${otherUser.firstName} ${otherUser.lastName}`
        : room.name;
      initials = otherUser
        ? `${otherUser.firstName?.[0] || ''}${
            otherUser.lastName?.[0] || ''
          }`.toUpperCase()
        : getInitials(room.name);
    }

    const avatarColor = stringToColor(displayName);
    const isSelected = selectedRoom?.id === room.id;
    const hasUnread = (room.unreadCount ?? 0) > 0 && !isSelected;
    let roomNameWeight = 400;
    if (hasUnread) roomNameWeight = 700;
    else if (isSelected) roomNameWeight = 600;
    const isOnline =
      isDirect && otherUser ? onlineUsers.has(otherUser.id) : false;

    // Last message preview
    const lastMsg = (room as any).lastMessage;
    const lastMsgPreview = lastMsg
      ? (lastMsg.translatedText || lastMsg.messageText || '').slice(0, 42)
      : null;
    const lastMsgTime = lastMsg
      ? (() => {
          const d = new Date(lastMsg.createdAt);
          const isToday = d.toDateString() === new Date().toDateString();
          return isToday
            ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
            : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
        })()
      : null;

    return (
      <ListItem
        button
        key={room.id}
        onClick={() => handleRoomSelect(room)}
        secondaryAction={
          <IconButton
            edge="end"
            onClick={(e) => handleTogglePin(room.id, room.isPinned || false, e)}
            size="small"
            sx={{
              '.MuiListItem-root:hover &': { opacity: 1 },
              opacity: room.isPinned ? 1 : 0,
              transition: 'opacity 0.2s'
            }}
          >
            <Tooltip title={room.isPinned ? 'Unpin' : 'Pin'}>
              {room.isPinned ? (
                <PushPin sx={{ color: 'warning.main', fontSize: 16 }} />
              ) : (
                <PushPinOutlined sx={{ fontSize: 16 }} />
              )}
            </Tooltip>
          </IconButton>
        }
        selected={isSelected}
        sx={{
          '&.Mui-selected': {
            '&:hover': { bgcolor: 'primary.light' },
            bgcolor: 'primary.light'
          },
          borderRadius: 1,
          mb: 0.25,
          px: 1,
          py: 0.75
        }}
      >
        {/* Avatar with online dot */}
        <ListItemAvatar sx={{ minWidth: 48 }}>
          <Box sx={{ display: 'inline-flex', position: 'relative' }}>
            {room.type === 'project' ? (
              <Box
                sx={{
                  alignItems: 'center',
                  bgcolor: 'primary.light',
                  borderRadius: '50%',
                  display: 'flex',
                  height: 36,
                  justifyContent: 'center',
                  width: 36
                }}
              >
                <CalendarMonth sx={{ color: 'primary.main', fontSize: 18 }} />
              </Box>
            ) : (
              <Avatar
                sx={{
                  bgcolor: avatarColor,
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  height: 36,
                  width: 36
                }}
              >
                {initials}
              </Avatar>
            )}
            {isOnline && (
              <Box
                sx={{
                  bgcolor: 'success.main',
                  border: '2px solid',
                  borderColor: 'background.paper',
                  borderRadius: '50%',
                  bottom: 0,
                  height: 10,
                  position: 'absolute',
                  right: 0,
                  width: 10
                }}
              />
            )}
          </Box>
        </ListItemAvatar>

        {/* Name row + preview row */}
        <ListItemText
          disableTypography
          primary={
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'space-between',
                pr: hasUnread ? 0 : 3
              }}
            >
              <Typography
                sx={{
                  flex: 1,
                  fontSize: '0.8125rem',
                  fontWeight: roomNameWeight,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {displayName}
              </Typography>
              <Box
                sx={{
                  alignItems: 'center',
                  display: 'flex',
                  flexShrink: 0,
                  gap: 0.5,
                  ml: 0.5
                }}
              >
                {lastMsgTime && (
                  <Typography
                    sx={{
                      color: hasUnread ? 'primary.main' : 'text.disabled',
                      fontSize: '0.65rem',
                      fontWeight: hasUnread ? 600 : 400
                    }}
                  >
                    {lastMsgTime}
                  </Typography>
                )}
                {hasUnread && (
                  <Box
                    sx={{
                      alignItems: 'center',
                      bgcolor: 'error.main',
                      borderRadius: '10px',
                      color: 'white',
                      display: 'flex',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      justifyContent: 'center',
                      minWidth: 18,
                      px: 0.5,
                      py: 0.125
                    }}
                  >
                    {room.unreadCount}
                  </Box>
                )}
              </Box>
            </Box>
          }
          secondary={
            lastMsgPreview ? (
              <Typography
                sx={{
                  color: hasUnread ? 'text.secondary' : 'text.disabled',
                  fontSize: '0.72rem',
                  fontWeight: hasUnread ? 500 : 400,
                  overflow: 'hidden',
                  pr: 3,
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {lastMsgPreview}
              </Typography>
            ) : null
          }
        />
      </ListItem>
    );
  };

  return (
    <Box sx={{ display: 'flex', height: 'calc(100vh - 64px)' }}>
      {/* CENTER: Message Thread */}
      <Box
        sx={{
          borderColor: 'divider',
          borderRight: '1px solid',
          display: 'flex',
          flex: 1,
          flexDirection: 'column'
        }}
      >
        {selectedRoom ? (
          <ChatThread
            joinRoom={joinRoom}
            key={`${selectedRoom.id}-${preferredLanguage}`}
            leaveRoom={leaveRoom}
            onParticipantsChanged={async () => {
              await loadRooms();
              // selectedRoom is stale after reload — refresh it from the store
              const updatedRoom = useChatStore
                .getState()
                .rooms.find((r) => r.id === selectedRoom?.id);
              if (updatedRoom) setSelectedRoom(updatedRoom);
            }}
            onRoomDeleted={() => {
              setSelectedRoom(null);
              loadRooms();
            }}
            onRoomRenamed={(roomId, newName) => {
              loadRooms().then(() => {
                setSelectedRoom((prev) =>
                  prev?.id === roomId ? { ...prev, name: newName } : prev
                );
              });
            }}
            room={selectedRoom}
            userId={userId}
            userLanguage={preferredLanguage}
            users={users}
          />
        ) : (
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              height: '100%',
              justifyContent: 'center'
            }}
          >
            <Typography color="text.secondary">
              Select a channel or chat to start messaging
            </Typography>
          </Box>
        )}
      </Box>

      {/* RIGHT SIDEBAR: Channels & Private Chats */}
      <Paper
        elevation={0}
        sx={{
          borderColor: 'divider',
          borderLeft: '1px solid',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          width: 280
        }}
      >
        {/* Language Selector */}
        <Box sx={{ p: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel>Language</InputLabel>
            <Select
              label="Language"
              onChange={handleLanguageChange}
              value={preferredLanguage}
            >
              <MenuItem value="en">English</MenuItem>
              <MenuItem value="es">Español</MenuItem>
              <MenuItem value="pl">Polski</MenuItem>
              <MenuItem value="ja">日本語</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider />

        {/* Channels Section */}
        <Box sx={{ p: 2 }}>
          <Box
            sx={{
              alignItems: 'center',
              display: 'flex',
              justifyContent: 'space-between',
              mb: 1.5
            }}
          >
            <Typography
              sx={{
                color: 'text.primary',
                fontSize: '0.75rem',
                fontWeight: 700,
                letterSpacing: '0.5px'
              }}
              variant="subtitle2"
            >
              CHANNELS
            </Typography>
            <Tooltip title="Create Channel">
              <IconButton
                onClick={handleOpenCreateDialog}
                size="small"
                sx={{ color: 'primary.main' }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
          <List dense disablePadding>
            {(showAllChannels ? channelRooms : channelRooms.slice(0, 10)).map(
              (room) => renderRoomItem(room, false)
            )}
          </List>
          {channelRooms.length > 10 && (
            <Button
              fullWidth
              onClick={() => setShowAllChannels(!showAllChannels)}
              size="small"
              sx={{ mt: 1 }}
            >
              {showAllChannels
                ? 'Show Less'
                : `Show ${channelRooms.length - 10} More`}
            </Button>
          )}
        </Box>

        <Divider />

        {/* Private Chats Section */}
        <Box sx={{ p: 2 }}>
          <Typography
            sx={{
              color: 'text.primary',
              fontSize: '0.75rem',
              fontWeight: 700,
              letterSpacing: '0.5px',
              mb: 1.5
            }}
            variant="subtitle2"
          >
            PRIVATE CHATS
          </Typography>
          <List dense disablePadding>
            {(showAllChats ? directRooms : directRooms.slice(0, 5)).map(
              (room) => renderRoomItem(room, true)
            )}
          </List>
          {directRooms.length > 5 && (
            <Button
              fullWidth
              onClick={() => setShowAllChats(!showAllChats)}
              size="small"
              sx={{ mt: 1 }}
            >
              {showAllChats
                ? 'Show Less'
                : `Show ${directRooms.length - 5} More`}
            </Button>
          )}
        </Box>
      </Paper>

      {/* Create Room Dialog */}
      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={handleCloseCreateDialog}
        open={createDialogOpen}
      >
        <DialogTitle>Create New Room</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            error={Boolean(createError)}
            helperText={createError || ' '}
            label="Room Name"
            margin="dense"
            onChange={(e) => {
              setNewRoomName(e.target.value);
              setCreateError('');
            }}
            value={newRoomName}
          />

          <FormControl fullWidth margin="dense">
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              onChange={(e) => setNewRoomType(e.target.value as any)}
              value={newRoomType}
            >
              <MenuItem value="group">Group Channel</MenuItem>
              <MenuItem value="direct">Direct Message</MenuItem>
            </Select>
          </FormControl>

          <Typography sx={{ mb: 1, mt: 2 }} variant="subtitle2">
            Add Participants:
          </Typography>

          <Autocomplete
            multiple
            filterOptions={(options, state) => {
              const inputValue = state.inputValue.toLowerCase();
              return options.filter(
                (option) =>
                  `${option.firstName} ${option.lastName}`
                    .toLowerCase()
                    .includes(inputValue) ||
                  option.email?.toLowerCase().includes(inputValue)
              );
            }}
            getOptionLabel={(option) =>
              `${option.firstName} ${option.lastName}`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(event, newValue) => {
              setSelectedParticipants(newValue);
            }}
            options={users.filter((user) => user.id !== userId)}
            renderInput={(params) => {
              /* eslint-disable react/jsx-no-duplicate-props */
              return (
                <TextField
                  InputLabelProps={params.InputLabelProps}
                  InputProps={params.InputProps}
                  disabled={params.disabled}
                  fullWidth={params.fullWidth}
                  helperText="Type to search and select participants"
                  id={params.id}
                  inputProps={params.inputProps}
                  label="Search participants"
                  size={params.size}
                />
              );
              /* eslint-enable react/jsx-no-duplicate-props */
            }}
            renderOption={(props, option) => {
              /* eslint-disable react/prop-types */
              const { key: optKey, ...liProps } =
                props as React.HTMLAttributes<HTMLLIElement> & { key: string };
              const { firstName, lastName, email } = option as {
                email?: string;
                firstName: string;
                lastName: string;
              };
              return (
                <React.Fragment key={optKey}>
                  {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                  <li {...liProps}>
                    <Box>
                      <Typography variant="body2">
                        {firstName} {lastName}
                      </Typography>
                      {email && (
                        <Typography color="text.secondary" variant="caption">
                          {email}
                        </Typography>
                      )}
                    </Box>
                  </li>
                </React.Fragment>
              );
              /* eslint-enable react/prop-types */
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, onDelete, disabled, className } = getTagProps({
                  index
                });
                return (
                  <Chip
                    className={className}
                    disabled={disabled}
                    key={key}
                    label={`${option.firstName} ${option.lastName}`}
                    onDelete={onDelete}
                  />
                );
              })
            }
            value={selectedParticipants}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>Cancel</Button>
          <Button onClick={handleCreateRoom} variant="contained">
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default ChatView;
